"use client"

import React, { createContext, useContext, useState, useEffect, ReactNode } from "react"
import { CompanyInfo, BranchInfo, CourseInfo, NominateInfo, UserInfo } from "@/types/driver_api_def"
import { getCompanyInfo } from "@/lib/driver_api"
import { useAuth } from "@/components/auth-provider"
import { logger } from "@/lib/logger"

interface CompanyContextType {
  companyInfo: CompanyInfo | null
  isLoading: boolean
  error: Error | null
  // ユーザー情報
  getUserInfo: () => UserInfo | null
  getUserFullName: () => string
  // ヘルパー関数
  getBranchName: (branchId: string) => string
  getBranchThemeColor: (branchId: string) => string
  getBranchThemeColorByName: (branchName: string) => string
  getBranchAbbreviation: (branchName: string) => string
  getCourseLabel: (courseId: number) => string
  getCourseAbbName: (courseId: number) => string
  getNominateLabel: (nominateId: number) => string
  // 全店舗リスト（フィルター用）
  getAllBranches: () => BranchInfo[]
  // 全コースリスト（フィルター用）
  getAllCourses: () => CourseInfo[]
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined)

export const CompanyProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isLoggedIn, isLoading: authLoading } = useAuth()
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    // 認証が完了するまでAPIを呼ばない
    if (authLoading || !isLoggedIn) {
      setIsLoading(false)
      return
    }

    const fetchCompanyInfo = async () => {
      try {
        setIsLoading(true)
        const data = await getCompanyInfo()
        setCompanyInfo(data)
      } catch (err) {
        logger.error("企業情報の取得に失敗しました", err)
        setError(err instanceof Error ? err : new Error("企業情報の取得に失敗しました"))
      } finally {
        setIsLoading(false)
      }
    }

    fetchCompanyInfo()
  }, [isLoggedIn, authLoading])

  // ユーザー情報を取得する関数
  const getUserInfo = (): UserInfo | null => {
    return companyInfo?.user ?? null
  }

  // ユーザーの氏名を取得する関数
  const getUserFullName = (): string => {
    return companyInfo?.user?.fullName ?? ""
  }

  // 店舗名を取得する関数
  const getBranchName = (branchId: string): string => {
    if (!companyInfo) return "不明な店舗"
    const branch = companyInfo.branches.find((b) => b.branchId === branchId)
    return branch ? branch.branchName : "不明な店舗"
  }

  // 店舗のテーマカラーを取得する関数
  const getBranchThemeColor = (branchId: string): string => {
    if (!companyInfo) return "gray"
    const branch = companyInfo.branches.find((b) => b.branchId === branchId)
    return branch ? branch.themeColor : "gray"
  }

  // 店舗名からテーマカラーを取得する関数
  const getBranchThemeColorByName = (branchName: string): string => {
    if (!companyInfo) return "gray"
    const branch = companyInfo.branches.find((b) => b.branchName === branchName)
    return branch ? branch.themeColor : "gray"
  }

  // 店舗名から略称を取得する関数
  const getBranchAbbreviation = (branchName: string): string => {
    if (!companyInfo) return branchName
    const branch = companyInfo.branches.find((b) => b.branchName === branchName)
    return branch?.branchNameAbb || branchName
  }

  // コース名を取得する関数
  const getCourseLabel = (courseId: number): string => {
    if (!companyInfo) return ""
    const course = companyInfo.courses.find((c) => c.courseId === courseId)
    if (course) return course.courseName
    return ""
  }

  // コース短縮名を取得する関数（例: "90分コース" → "90分"）
  const getCourseAbbName = (courseId: number): string => {
    const label = getCourseLabel(courseId)
    // "XXコース" の "コース" を除去
    return label.replace(/コース$/, "")
  }

  // 指名タイプ名を取得する関数
  const getNominateLabel = (nominateId: number): string => {
    if (!companyInfo) return ""
    const nominate = companyInfo.nominates.find((n) => n.nominateId === nominateId)
    if (nominate) return nominate.nominateName
    return ""
  }

  // 全店舗リストを取得（フィルター用）
  const getAllBranches = (): BranchInfo[] => {
    return companyInfo?.branches || []
  }

  // 全コースリストを取得（重複除去、ソート済み）
  const getAllCourses = (): CourseInfo[] => {
    if (!companyInfo) return []
    return companyInfo.courses.sort((a, b) => a.sortOrder - b.sortOrder)
  }

  const value: CompanyContextType = {
    companyInfo,
    isLoading,
    error,
    getUserInfo,
    getUserFullName,
    getBranchName,
    getBranchThemeColor,
    getBranchThemeColorByName,
    getBranchAbbreviation,
    getCourseLabel,
    getCourseAbbName,
    getNominateLabel,
    getAllBranches,
    getAllCourses,
  }

  return <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>
}

// カスタムフック: useCompany
export const useCompany = (): CompanyContextType => {
  const context = useContext(CompanyContext)
  if (context === undefined) {
    throw new Error("useCompany must be used within a CompanyProvider")
  }
  return context
}
